// Менюто на „Складова база" — ЕДИНСТВЕНОТО място, което казва кои страници има,
// в коя секция са и с какви значки. Sidebar-ът го рисува, а Inventory.tsx
// проверява срещу него, че всеки slug има страница.
//
// Секциите разделят РАБОТАТА от ПРЕГЛЕДА. Най-горе е „Табло" — началната
// страница с по една кутия на операция (живото състояние + основното действие);
// под него операциите са СГЪВАЕМА група (свита по подразбиране, изборът се
// помни — `useNavGroupOpen`), защото от таблото се стига до всяка с един клик
// или клавиш 1–8. Следват Справки (търсиш, филтрираш, печаташ) и Настройки.
// Документите на една операция живеят в нейната справка: Доставка → Доставки,
// Разнасяне → Разнесени отчети…
import type { ComponentType } from "react";
import {
  AppstoreOutlined,
  AuditOutlined,
  ContainerOutlined,
  DashboardOutlined,
  DeleteOutlined,
  ExportOutlined,
  FileDoneOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  InboxOutlined,
  InteractionOutlined,
  NodeIndexOutlined,
  ProfileOutlined,
  ReconciliationOutlined,
  RetweetOutlined,
  SettingOutlined,
  ShoppingOutlined,
  SwapOutlined,
  WalletOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { strongestSignal, type SignalTone } from "../list/signals";

// Кой брояч носи значката на реда (виж useSidebarBadges). `attentionTotal` е
// сборът от значките на операциите — носи го „Табло", за да се вижда и при
// свита група. Черновите (`*Drafts`) са незавършените листове на ресторанта —
// мои и на колегите.
export type InventoryNavBadge =
  | "pendingReports"
  | "deliveryDrafts"
  | "unpaidDeliveries"
  | "writeoffDrafts"
  | "stocktakeDrafts"
  | "transferDrafts"
  | "attentionTotal";

// Броячи, които не са значка на ред, а оцветяват значка: колко от чакащите
// отчети са закъснели (червената значка на „Разнасяне")
export type InventoryNavBadgeModifier = "overduePendingReports";

export type NavBadgeCounts = Partial<Record<InventoryNavBadge | InventoryNavBadgeModifier, number>>;

export type InventoryNavItem = {
  slug: string;
  labelKey: string;
  Icon: ComponentType<{ className?: string }>;
  badge?: InventoryNavBadge;
};

// Сгъваема група под редовете на секцията (операциите под „Табло")
export type InventoryNavGroup = {
  key: string;
  labelKey: string;
  items: InventoryNavItem[];
};

export type InventoryNavSection = {
  // Без заглавие — първата секция („Табло" стои сам най-горе)
  titleKey?: string;
  items: InventoryNavItem[];
  group?: InventoryNavGroup;
};

export const DASHBOARD_SLUG = "dashboard";

// Операциите (въвеждаш документ) — в реда на кутиите в таблото (клавиши 1–8)
export const OPERATIONS_NAV_GROUP: InventoryNavGroup = {
  key: "operations",
  labelKey: "inventoryPage.sectionOperations",
  items: [
    {
      slug: "posting",
      labelKey: "inventoryPage.posting",
      Icon: FileDoneOutlined,
      badge: "pendingReports",
    },
    {
      slug: "delivery",
      labelKey: "inventoryPage.delivery",
      Icon: InboxOutlined,
      badge: "deliveryDrafts",
    },
    {
      slug: "delivery-payments",
      labelKey: "inventoryPage.navDeliveryPayments",
      Icon: WalletOutlined,
      badge: "unpaidDeliveries",
    },
    {
      slug: "writeoff",
      labelKey: "inventoryPage.writeoff",
      Icon: ExportOutlined,
      badge: "writeoffDrafts",
    },
    {
      slug: "stocktake",
      labelKey: "inventoryPage.stocktake",
      Icon: ReconciliationOutlined,
      badge: "stocktakeDrafts",
    },
    {
      slug: "transfer",
      labelKey: "inventoryPage.transfer",
      Icon: RetweetOutlined,
      badge: "transferDrafts",
    },
    {
      slug: "shortage",
      labelKey: "inventoryPage.negativeReport",
      Icon: WarningOutlined,
    },
    {
      slug: "interchangeables",
      labelKey: "inventoryPage.interchange",
      Icon: SwapOutlined,
    },
  ],
};

export const INVENTORY_NAV: InventoryNavSection[] = [
  {
    items: [
      {
        slug: DASHBOARD_SLUG,
        labelKey: "inventoryPage.dashNav",
        Icon: DashboardOutlined,
        badge: "attentionTotal",
      },
    ],
    group: OPERATIONS_NAV_GROUP,
  },
  {
    titleKey: "inventoryPage.sectionReports",
    items: [
      {
        slug: "posted-reports",
        labelKey: "inventoryPage.navPostedReports",
        Icon: FileSearchOutlined,
      },
      {
        slug: "deliveries",
        labelKey: "inventoryPage.navDeliveriesReport",
        Icon: ProfileOutlined,
      },
      {
        slug: "writeoffs",
        labelKey: "inventoryPage.navWriteoffsReport",
        Icon: FileTextOutlined,
      },
      {
        slug: "stocktakes",
        labelKey: "inventoryPage.navStocktakesReport",
        Icon: AuditOutlined,
      },
      {
        slug: "transfers",
        labelKey: "inventoryPage.navTransfersReport",
        Icon: NodeIndexOutlined,
      },
      {
        slug: "sold-products",
        labelKey: "inventoryPage.soldProducts",
        Icon: ShoppingOutlined,
      },
      {
        slug: "sold-recipes",
        labelKey: "inventoryPage.soldRecipes",
        Icon: ContainerOutlined,
      },
      { slug: "stock", labelKey: "inventoryPage.stockReport", Icon: AppstoreOutlined },
      {
        slug: "substitutions",
        labelKey: "inventoryPage.substitutionsReport",
        Icon: InteractionOutlined,
      },
      { slug: "deleted", labelKey: "inventoryPage.deletedReport", Icon: DeleteOutlined },
    ],
  },
  {
    titleKey: "inventoryPage.sectionConfig",
    items: [
      { slug: "settings", labelKey: "inventoryPage.settings", Icon: SettingOutlined },
    ],
  },
];

// Началната страница на модула: таблото — какво чака и откъде да започнеш.
export const DEFAULT_INVENTORY_SLUG = DASHBOARD_SLUG;

// Всички редове на секцията, и тези в групата
export const sectionItemsOf = (section: InventoryNavSection): InventoryNavItem[] => [
  ...section.items,
  ...(section.group?.items ?? []),
];

export const INVENTORY_NAV_SLUGS = INVENTORY_NAV.flatMap((section) =>
  sectionItemsOf(section).map((item) => item.slug),
);

// Сборът от значките на операциите — значката на „Табло"
export const operationsBadgeTotal = (badges: NavBadgeCounts): number =>
  OPERATIONS_NAV_GROUP.items.reduce(
    (sum, item) => sum + (item.badge ? badges[item.badge] ?? 0 : 0),
    0,
  );

// Тонът на значката (docs/inventory.md §Плътност и сигнали): незавършената
// инвентаризация — винаги червено (при всички потребители); чакащите отчети —
// червено при закъснял, иначе меко кехлибарено; неплатените — кехлибарено;
// днешните чернови на доставка, изписване и прехвърляне — неутрален брояч.
// „Табло" взема най-силния тон от операциите. Без брой — без тон.
export const navBadgeToneOf = (badge: InventoryNavBadge, badges: NavBadgeCounts): SignalTone | null => {
  if (badge === "attentionTotal") {
    return strongestSignal(
      OPERATIONS_NAV_GROUP.items.map((item) =>
        item.badge && item.badge !== "attentionTotal" ? navBadgeToneOf(item.badge, badges) : null,
      ),
    );
  }
  if (!(badges[badge] ?? 0)) return null;
  switch (badge) {
    case "stocktakeDrafts":
      return "critical";
    case "pendingReports":
      return (badges.overduePendingReports ?? 0) > 0 ? "critical" : "attention";
    case "unpaidDeliveries":
      return "attention";
    default:
      return "neutral";
  }
};

// Редовете на групата, които менюто показва: всички при отворена група, иначе
// само активният (менюто казва къде си и при свита група)
export const visibleGroupItems = (
  group: InventoryNavGroup,
  open: boolean,
  activeSlug: string,
): InventoryNavItem[] =>
  open ? group.items : group.items.filter((item) => item.slug === activeSlug);
